import { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { IN_APP_FOOTER_LINKS } from './publicFooterLinks';

// Modeled on kolega.dev/refund (KLG Tech Innovations Limited) and adapted for Kolega Studio.
// Paddle-ready: names Paddle as Merchant of Record.
const PADDLE_REFUND_URL = 'https://www.paddle.com/help/manage/your-customers/buyers-refunds';

function PublicPageShell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-white dark:bg-neutral-950 text-neutral-900 dark:text-white flex flex-col">
      <header className="border-b border-neutral-200 dark:border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="text-base sm:text-lg font-bold flex items-center">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="mr-1.5 sm:mr-2">
              <path d="M7 8L3 12L7 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M17 8L21 12L17 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M10 20L14 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>Kolega</span>
            <span className="font-light">Studio</span>
          </Link>
          <Link
            to="/"
            className="bg-brand-primary text-black text-sm font-medium px-4 py-2 rounded-lg hover:bg-brand-primaryDark transition-colors"
          >
            Sign in
          </Link>
        </div>
      </header>
      <div className="flex-1">{children}</div>
      <footer className="border-t border-neutral-200 dark:border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-neutral-600 dark:text-neutral-400">
          {IN_APP_FOOTER_LINKS.map((link) => (
            <Link key={link.href} to={link.href} className="hover:text-neutral-900 dark:hover:text-white transition-colors">
              {link.label}
            </Link>
          ))}
        </div>
        <div className="pb-6 text-center text-xs text-neutral-500 dark:text-neutral-500">
          &copy; {new Date().getFullYear()} Kolega Studio. All rights reserved.
        </div>
      </footer>
    </div>
  );
}

const LINK_CLASSES = 'text-brand-primaryDark dark:text-brand-primary hover:underline';

export default function RefundPolicyPage() {
  return (
    <PublicPageShell>
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 text-neutral-700 dark:text-neutral-300">
        <h1 className="text-3xl font-bold text-neutral-900 dark:text-white mb-2">Refund Policy</h1>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-8">Last Updated: August 11, 2026</p>

        <div className="space-y-6 text-sm">
          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">1. Introduction</h3>
            <p className="mb-3">
              This Refund Policy explains how refunds, cancellations, and statutory withdrawal rights work for
              Kolega Studio, the AI-assisted application building platform and services (&quot;Service&quot;) operated by
              KLG Tech Innovations Limited (&quot;Kolega,&quot; &quot;we,&quot; &quot;us&quot;).
            </p>
            <p>
              This policy applies to individual users of our Service on the Creator and Pro tiers. Custom-plan
              customers are subject to the refund and cancellation terms set out in their individual contracts.
              This policy should be read together with our{' '}
              <Link to="/terms" className={LINK_CLASSES}>Terms of Service</Link> and{' '}
              <Link to="/privacy" className={LINK_CLASSES}>Privacy Policy</Link>. All fees are displayed
              and charged in USD unless otherwise shown based on your location.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">2. Merchant of Record</h3>
            <p className="mb-3">
              All payments for the Service are processed by Paddle, who acts as the Merchant of Record (MoR) for every
              transaction. This means that when you purchase a subscription, credit top-up, or custom domain, your
              contract for the purchase is with Paddle, and Paddle (not Kolega) appears on your billing statement.
            </p>
            <p className="mb-3">As Merchant of Record, Paddle handles:</p>
            <ul className="list-disc pl-6 space-y-1 mb-3">
              <li>Payment processing and card authorisation</li>
              <li>Invoicing and receipts</li>
              <li>Billing and payment support</li>
              <li>Global sales tax, VAT, and GST compliance</li>
            </ul>
            <p>
              Because Paddle processes your transaction, your purchase is also governed by the{' '}
              <a href={PADDLE_REFUND_URL} target="_blank" rel="noopener noreferrer" className={LINK_CLASSES}>
                Paddle Checkout Buyer Terms and Conditions
              </a>
              . We recommend reviewing these terms, as they set out additional rights and obligations relating to your payment.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">3. Statutory Consumer Cancellation Rights</h3>
            <p className="mb-3">
              Paddle enforces consumer protection laws that vary depending on where you are located. Regardless of any
              other provision in this policy, you are always entitled to the minimum rights guaranteed by the law in your
              jurisdiction. The regional minimums below apply:
            </p>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2">3.1 EU, UK, EEA, and Switzerland — 14-Day Right to Withdraw</h4>
            <p className="mb-3">
              If you are a consumer in the European Union, United Kingdom, European Economic Area, or Switzerland, you
              have a statutory right to withdraw from a digital content or service contract within 14 days of purchase and
              receive a full refund, subject to the waiver conditions in Section 4.
            </p>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2">3.2 United Kingdom — Auto-Renewal Refund Window</h4>
            <p className="mb-3">
              If you are a UK consumer on an annual subscription, a new 14-day refund window is triggered each time your
              annual subscription automatically renews. You may request a full refund of the renewal charge within 14 days
              of the renewal date, subject to the waiver conditions in Section 4.
            </p>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2">3.3 South Korea, Brazil, China, and Canada — 7-Day Right to Cancel</h4>
            <p>
              If you are a consumer in South Korea, Brazil, China, or Canada, you have a 7-day unconditional right to cancel
              a digital services purchase after delivery and receive a full refund, subject to the waiver conditions in Section 4.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">4. Refund Eligibility and Waiver of Rights</h3>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2">4.1 Immediate Waiver Upon Use of Digital Content</h4>
            <p className="mb-3">
              Our Service delivers digital content and is made available to you immediately upon purchase. Where you
              expressly consent to begin using the Service before the end of your statutory withdrawal period — and
              acknowledge that you lose your right to withdraw by doing so — your 14-day (or 7-day) refund window is voided
              once you begin meaningfully accessing, downloading, streaming, or using the digital content. You provide this
              consent at checkout.
            </p>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2">4.2 Meaningful Consumption (SaaS and Credit-Based Usage)</h4>
            <p className="mb-3">
              Because Kolega Studio is a usage-based platform, trivial or trial use of the Service does not, on its own,
              forfeit your refund eligibility. However, meaningful consumption of core resources makes your purchase
              non-refundable. Core resources include, without limitation:
            </p>
            <ul className="list-disc pl-6 space-y-1 mb-3">
              <li>Consuming usage credits by running AI agents to build, edit, or iterate on your applications</li>
              <li>Deploying or hosting applications through the Service</li>
              <li>Consuming purchased credit top-ups</li>
              <li>Registering or using a custom domain purchased through the Service</li>
            </ul>
            <p className="mb-3">
              Once you have consumed core resources beyond trivial or trial use, the corresponding purchase is treated as
              delivered and is not eligible for a refund, except where a non-waivable statutory right applies.
            </p>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2">4.3 Unused Credit Top-Ups</h4>
            <p className="mb-3">
              Credits are consumed on use. If you purchase a credit top-up and have not consumed any of the purchased
              credits, you may request a full refund of that top-up within 14 days of purchase. Once any portion of a
              purchased top-up has been consumed, it is treated as delivered under Section 4.2.
            </p>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2">4.4 Custom Domain Registrations</h4>
            <p className="mb-3">
              Custom domain registrations are non-refundable once registration with the domain registry has started.
              Registry fees are incurred immediately and are non-recoverable, so domain purchases cannot be reversed
              once the registration process has begun, except where a non-waivable statutory right applies.
            </p>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2">4.5 Sales Are Final After the Statutory Window</h4>
            <p>
              After your applicable statutory cancellation window (Section 3) has expired, all sales are final. Consistent
              with our Terms of Service, fees are non-refundable except where required by law.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">5. Subscriptions and Auto-Renewal</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <span className="font-medium text-neutral-900 dark:text-white">Cancellation stops future billing only.</span>{' '}
                Cancelling your subscription prevents future charges but does not trigger a retroactive refund for the
                current or any past billing period.
              </li>
              <li>
                <span className="font-medium text-neutral-900 dark:text-white">No prorated refunds.</span> We do not offer
                prorated refunds for cancellations made part-way through a billing cycle. You retain access to your paid
                features until the end of the period you have already paid for.
              </li>
              <li>
                <span className="font-medium text-neutral-900 dark:text-white">No rollover.</span> Consistent with our Terms
                of Service, unused included monthly credits do not roll over to the following month and are not refundable.
              </li>
              <li>
                <span className="font-medium text-neutral-900 dark:text-white">Free signup credits.</span> Free signup
                credits granted to new accounts have no monetary value, expire automatically (14 days after grant), and are
                not refundable or redeemable for cash. No card is required and no charge is made for signup credits.
              </li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">6. How to Request a Refund</h3>
            <p className="mb-3">To keep refunds fast and to avoid unnecessary disputes, please follow these steps:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>
                <span className="font-medium text-neutral-900 dark:text-white">Contact us first.</span> Email our support team at{' '}
                <a href="mailto:contact@kolega.ai" className={LINK_CLASSES}>contact@kolega.ai</a> so we can
                review your request and resolve it directly wherever possible.
              </li>
              <li>
                <span className="font-medium text-neutral-900 dark:text-white">Use the Paddle Order Support Portal.</span>{' '}
                Because Paddle is the Merchant of Record, you may also submit a refund request directly through the{' '}
                <a href={PADDLE_REFUND_URL} target="_blank" rel="noopener noreferrer" className={LINK_CLASSES}>
                  Paddle Order Support Portal
                </a>
                . You will need your order reference or the email address used at checkout.
              </li>
            </ul>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2">Refund Timelines</h4>
            <p className="mb-3">
              Approved refunds are routed exclusively back to the original payment method used at the time of purchase.
              Typical processing times are:
            </p>
            <ul className="list-disc pl-6 space-y-1 mb-3">
              <li>Cards: approximately 3 to 5 business days</li>
              <li>PayPal: approximately 48 hours</li>
            </ul>
            <p>
              Exact timing depends on your bank or payment provider and is outside our and Paddle&apos;s direct control.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">7. Chargebacks and Disputes</h3>
            <p className="mb-3">
              Paddle actively monitors chargeback rates across its merchant network. Opening a formal bank dispute or
              chargeback before contacting us removes our ability to resolve the issue quickly and may cause unnecessary delays.
            </p>
            <p className="mb-3">
              Please contact our support team at{' '}
              <a href="mailto:contact@kolega.ai" className={LINK_CLASSES}>contact@kolega.ai</a> before
              opening any formal bank dispute or chargeback. Almost all issues can be resolved faster directly.
            </p>
            <p>
              We reserve the right to temporarily or permanently suspend or terminate any account that initiates an
              unannounced chargeback, in addition to any other remedies available to us under our Terms of Service.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">8. Questions and Contact</h3>
            <p className="mb-3">
              If you have questions about this Refund Policy, a specific charge, or your refund eligibility, please contact
              us before taking any other action:
            </p>
            <div className="bg-neutral-100 dark:bg-neutral-900 p-4 rounded-lg">
              <p className="font-medium text-neutral-900 dark:text-white">KLG Tech Innovations Limited</p>
              <p>Val Verclut, La Route des Cotils</p>
              <p>Grouville, JE3 9AP, Jersey</p>
              <p>
                Email: <a href="mailto:contact@kolega.ai" className={LINK_CLASSES}>contact@kolega.ai</a>
              </p>
            </div>
            <p className="mt-3">
              For payment, invoicing, and billing matters, you may also contact Paddle as Merchant of Record via the{' '}
              <a href={PADDLE_REFUND_URL} target="_blank" rel="noopener noreferrer" className={LINK_CLASSES}>
                Paddle Order Support Portal
              </a>
              .
            </p>
          </section>

          <div className="border-t border-neutral-200 dark:border-neutral-800 pt-4 text-center text-xs text-neutral-500 dark:text-neutral-400">
            &copy; {new Date().getFullYear()} Kolega Studio. All rights reserved.
          </div>
        </div>
      </main>
    </PublicPageShell>
  );
}
